import cls from "@fvilers/cls";
import { MinusIcon } from "@imi/icons/MinusIcon.js";
import { PlusIcon } from "@imi/icons/PlusIcon.js";
import { useFieldContext } from "../../hooks/useAppForm";
import { useIdOr } from "../../hooks/useIdOr";
import { useTimeoutRef } from "../../hooks/useTimeoutRef";
import { AnimatedValue } from "../AnimatedValue/AnimatedValue";
import { FieldError, FieldErrors } from "../FieldErrors/FieldErrors";
import { TouchTarget } from "../TouchTarget/TouchTarget";
import styles from "./Stepper.module.css";

export type StepperProps = {
  className?: string;
  errors?: FieldError;
  id?: string;
  label?: string;
  max?: number;
  min?: number;
  onChange?: (value: number) => void;
  onChangeDebounce?: number;
  step?: number;
  value?: number;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "max" | "min" | "onChange" | "step" | "type" | "value"
>;

export function Stepper({
  className,
  errors,
  id: propsId,
  label,
  max = Infinity,
  min = -Infinity,
  onChange,
  onChangeDebounce,
  step = 1,
  value,
  ...inputProps
}: StepperProps) {
  const timeoutRef = useTimeoutRef();
  const currentValue = Number(value ?? 0);
  const hasErrors = Boolean(
    errors && (Array.isArray(errors) ? errors.length : true),
  );
  const id = useIdOr(propsId);
  const errorId = hasErrors ? `${id}-error` : undefined;

  function handleChange(value: number) {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (onChangeDebounce) {
      timeoutRef.current = setTimeout(
        () => onChange?.(value),
        onChangeDebounce,
      );
    } else {
      onChange?.(value);
    }
  }

  function increment() {
    const next = Math.min(Number(max), currentValue + Number(step));
    handleChange?.(next);
  }

  function decrement() {
    const next = Math.max(Number(min), currentValue - Number(step));
    handleChange?.(next);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      increment();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      decrement();
    }
  }

  return (
    <FieldErrors
      className={cls(styles.Stepper, className)}
      data-slot="control"
      errorId={errorId}
      errors={errors}
    >
      <div className={styles.StepperWrapper}>
        <button
          aria-label="Decrement"
          data-testid={`${inputProps.name ?? propsId}-decrement`}
          id={`${inputProps.name ?? propsId}-decrement`}
          onClick={decrement}
          type="button"
        >
          <TouchTarget>
            <MinusIcon />
          </TouchTarget>
        </button>

        <div className={styles.InputWrapper}>
          <input
            aria-describedby={errorId ?? undefined}
            aria-invalid={!!hasErrors}
            aria-valuemax={max}
            aria-valuemin={min}
            aria-valuenow={currentValue}
            id={id}
            onKeyDown={handleKeyDown}
            readOnly
            value={value}
            {...inputProps}
            data-slot="stepper"
            type="text"
          />
          <AnimatedValue value={value} />
        </div>

        <button
          aria-label="Increment"
          data-testid={`${inputProps.name ?? propsId}-increment`}
          id={`${inputProps.name ?? propsId}-increment`}
          onClick={increment}
          type="button"
        >
          <TouchTarget>
            <PlusIcon />
          </TouchTarget>
        </button>
      </div>
      {label && <label htmlFor={id}>{label}</label>}
    </FieldErrors>
  );
}

export function StepperField(props: StepperProps) {
  const field = useFieldContext<number>();
  return (
    <Stepper
      errors={field.state.meta.errors.map((e) =>
        typeof e === "string" ? e : e?.message,
      )}
      {...props}
      form={field.form.formId}
      id={props.id ?? field.name}
      name={field.name}
      onBlur={field.handleBlur}
      onChange={(value) => {
        field.handleChange(value);
        props.onChange?.(value);
      }}
      value={field.state.value}
    />
  );
}
